import { useMemo } from 'react';
import { Gate } from '../components/shell/gate.tsx';
import {
  RecordTable,
  type ColumnDef,
  type RecordSource,
} from '../components/record-table/index.ts';
import { api, type AuditEntry } from '../lib/client.ts';
import { useSession } from '../lib/session.tsx';
import { useSettings } from '../lib/settings.ts';
import { PageHeader } from '../components/ui/page-header.tsx';

export const auditColumns: ColumnDef<AuditEntry, unknown>[] = [
  {
    accessorKey: 'occurredAt',
    header: 'When',
    size: 180,
    cell: ({ row }) => new Date(row.original.occurredAt).toLocaleString(),
  },
  { accessorKey: 'action', header: 'Action', size: 120 },
  { accessorKey: 'entityType', header: 'Entity', size: 140 },
  { accessorKey: 'entityId', header: 'Id', size: 300 },
  {
    id: 'actor',
    header: 'Actor',
    size: 220,
    accessorFn: (r) => r.actor.label ?? r.actor.id ?? r.actor.type,
  },
  { accessorKey: 'source', header: 'Source', size: 100 },
];

/** The API's own cursor-paged list, straight into the table: `/audit?cursor&limit&entityType`. */
export function auditSource(organisationId: string): RecordSource<AuditEntry> {
  return {
    key: `audit:${organisationId}`,
    sortable: [], // the API orders newest-first; there is no other order to ask for
    async fetchPage({ cursor, limit, filter }) {
      const page = await api.audit({
        limit,
        ...(cursor ? { cursor } : {}),
        ...(filter.trim() ? { entityType: filter.trim() } : {}),
      });
      return { items: page.items, nextCursor: page.nextCursor };
    },
  };
}

export function AuditPage() {
  const session = useSession();
  const [settings] = useSettings(session.me.user.id);
  const source = useMemo(
    () => auditSource(session.me.organisation.id),
    [session.me.organisation.id],
  );
  return (
    <Gate permission="audit_log.read" feature="audit_log.enabled">
      <PageHeader
        title="Audit log"
        description="Every change, newest first: who made it, to what, and from where."
      />
      <RecordTable
        columns={auditColumns}
        source={source}
        getRowId={(r) => r.id}
        mode={settings.pagination}
        pageSize={settings.pageSize}
        filterPlaceholder="Filter by entity type (e.g. candidate)…"
        emptyMessage="Nothing audited yet."
      />
    </Gate>
  );
}
